import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  ChefHat, 
  BarChart3, 
  Package, 
  Brain, 
  ArrowRight, 
  Check,
  Smartphone,
  Users,
  Clock
} from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const [hoveredFeature, setHoveredFeature] = useState(null);

  const features = [
    {
      icon: ChefHat,
      title: 'Terminal POS Intuitivo',
      description: 'Interfaz táctil optimizada para tablets. Toma órdenes en segundos con nuestro diseño tipo Bento.',
      color: 'text-accent'
    },
    {
      icon: BarChart3,
      title: 'Analítica en Tiempo Real',
      description: 'Dashboards ejecutivos con KPIs financieros, tendencias y métricas que importan.',
      color: 'text-success'
    },
    {
      icon: Package,
      title: 'Inventario Inteligente',
      description: 'Control de stock automatizado con alertas de bajo inventario y seguimiento de costos.',
      color: 'text-primary'
    },
    {
      icon: Brain,
      title: 'IA Predictiva',
      description: 'Predicciones de ventas, optimización de compras y recomendaciones automáticas.',
      color: 'text-accent'
    }
  ];

  const benefits = [
    { icon: Smartphone, text: 'Diseñado para tablets' },
    { icon: Users, text: 'Multi-usuario' },
    { icon: Clock, text: 'Servicio 24/7' }
  ];

  const pricing = [
    {
      name: 'Básico',
      price: '$499',
      period: '/mes',
      features: ['POS completo', 'Gestión de mesas', 'Reportes básicos', 'Soporte email'],
      popular: false
    },
    {
      name: 'Profesional',
      price: '$999',
      period: '/mes',
      features: ['Todo en Básico', 'Inventario avanzado', 'Analítica completa', 'IA Predictiva', 'Soporte prioritario'],
      popular: true
    },
    {
      name: 'Empresarial',
      price: 'Contactar',
      period: '',
      features: ['Todo en Profesional', 'Multi-sucursal', 'API personalizada', 'Gerente de cuenta', 'Implementación dedicada'],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                <ChefHat className="w-6 h-6 text-white" />
              </div>
              <span className="font-chivo font-extrabold text-xl">SmartPOS</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" data-testid="login-nav-btn">Iniciar Sesión</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-accent text-white hover:bg-accent/90 shadow-lg shadow-accent/20" data-testid="register-nav-btn">
                  Prueba Gratis
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 rounded-full bg-accent/10 text-accent text-sm font-medium mb-6">
                Plataforma SaaS para Restaurantes
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-chivo font-extrabold tracking-tight mb-6 text-foreground">
                El POS que <span className="text-accent">piensa</span> por ti
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-xl">
                Transforma la operación de tu restaurante con inteligencia artificial. 
                No solo registres ventas, genera insights accionables para maximizar tu rentabilidad.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="bg-accent text-white hover:bg-accent/90 shadow-lg shadow-accent/20 font-bold"
                  onClick={() => navigate('/register')}
                  data-testid="hero-cta-btn"
                >
                  Comenzar Ahora <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => navigate('/login')}
                  data-testid="hero-demo-btn"
                >
                  Ver Demo
                </Button>
              </div>
              <div className="flex items-center gap-6 mt-8">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <benefit.icon className="w-4 h-4 text-accent" />
                    <span>{benefit.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1753918256348-b336dd9d500b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjByZXN0YXVyYW50JTIwY2hlZiUyMHBsYXRpbmclMjBmb29kJTIwa2l0Y2hlbnxlbnwwfHx8fDE3NzA4NTIzNDJ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Cocina moderna de restaurante"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="glass rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Ventas de hoy</p>
                        <p className="text-2xl font-chivo font-extrabold">$12,450</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Órdenes</p>
                        <p className="text-2xl font-chivo font-extrabold text-success">+23%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 md:px-8 lg:px-12 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-chivo font-extrabold mb-4">
              Todo lo que necesitas para tu restaurante
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Una plataforma completa que integra operaciones, analítica e inteligencia artificial
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-card p-6 rounded-xl border border-border/50 hover:border-accent hover:shadow-lg transition-all duration-300 cursor-pointer"
                onMouseEnter={() => setHoveredFeature(idx)}
                onMouseLeave={() => setHoveredFeature(null)}
                data-testid={`feature-card-${idx}`}
              >
                <div className={`w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4 ${hoveredFeature === idx ? 'scale-110' : ''} transition-transform`}>
                  <feature.icon className={`w-6 h-6 ${feature.color}`} />
                </div>
                <h3 className="font-chivo font-extrabold text-lg mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-chivo font-extrabold mb-4">
              Planes que crecen contigo
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Elige el plan que mejor se adapte a tu restaurante. Sin contratos de permanencia.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {pricing.map((plan, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className={`relative bg-card p-8 rounded-xl border ${plan.popular ? 'border-accent shadow-lg shadow-accent/10' : 'border-border/50'}`}
                data-testid={`pricing-card-${idx}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-accent text-white text-xs font-bold px-3 py-1 rounded-full">
                      Más Popular
                    </span>
                  </div>
                )}
                <h3 className="font-chivo font-extrabold text-xl mb-2">{plan.name}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-chivo font-extrabold">{plan.price}</span>
                  <span className="text-muted-foreground">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fIdx) => (
                    <li key={fIdx} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-success" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className={`w-full ${plan.popular ? 'bg-accent text-white hover:bg-accent/90' : ''}`}
                  variant={plan.popular ? 'default' : 'outline'}
                  onClick={() => navigate('/register')}
                  data-testid={`pricing-btn-${idx}`}
                >
                  {plan.price === 'Contactar' ? 'Contactar Ventas' : 'Comenzar'}
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 md:px-8 lg:px-12 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-chivo font-extrabold mb-4">
            ¿Listo para transformar tu restaurante?
          </h2>
          <p className="text-primary-foreground/70 mb-8 max-w-2xl mx-auto">
            Únete a cientos de restaurantes que ya optimizan sus operaciones con SmartPOS
          </p>
          <Button 
            size="lg" 
            className="bg-accent text-white hover:bg-accent/90 shadow-lg shadow-accent/20 font-bold"
            onClick={() => navigate('/register')}
            data-testid="cta-btn"
          >
            Comenzar Prueba Gratuita <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 md:px-8 lg:px-12 border-t border-border/50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
                <ChefHat className="w-4 h-4 text-white" />
              </div>
              <span className="font-chivo font-extrabold">SmartPOS</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2024 SmartPOS. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
